import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight, Calendar, Image, Lock, Eye, Users, Upload, X } from 'lucide-react';
import { motion } from 'framer-motion';
import { VIBES } from '../lib/moodIcons';
import { OCCASIONS } from '../lib/occasions';
import CoverCropper from '../components/CoverCropper';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRIVACY_MODES = [
  { value: 'open', icon: <Users className="w-5 h-5" />, label: 'Open', desc: 'Anyone can view & upload' },
  { value: 'locked', icon: <Lock className="w-5 h-5" />, label: 'Locked', desc: 'Password required' },
  { value: 'moderated', icon: <Eye className="w-5 h-5" />, label: 'Moderated', desc: 'You approve uploads' },
];

export default function CreateAuraPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [multiDay, setMultiDay] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [occasion, setOccasion] = useState('');
  const [vibe, setVibe] = useState('elegant');
  const [privacyMode, setPrivacyMode] = useState('open');
  const [password, setPassword] = useState('');
  const [coverImage, setCoverImage] = useState('');
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);
  const [cropSrc, setCropSrc] = useState(null);  // dataURL of local file awaiting crop
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Step 1: user picks a file → load as data URL → open the cropper modal.
  // EXIF auto-orient: createImageBitmap with imageOrientation:'from-image' is
  // honoured by all modern browsers and is the cheapest way to normalise HEIC
  // and rotated JPEGs (orientation tag 6/8) without an EXIF dep.
  const handleCoverFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Cover must be an image');
      return;
    }
    setError('');
    try {
      let dataUrl = null;
      if (typeof createImageBitmap === 'function') {
        try {
          const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
          const canvas = document.createElement('canvas');
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
          canvas.getContext('2d').drawImage(bitmap, 0, 0);
          dataUrl = canvas.toDataURL('image/jpeg', 0.92);
          bitmap.close?.();
        } catch (_) { /* fall through to FileReader */ }
      }
      if (dataUrl) {
        setCropSrc(dataUrl);
      } else {
        const reader = new FileReader();
        reader.onload = () => setCropSrc(reader.result);
        reader.onerror = () => setError('Could not read file');
        reader.readAsDataURL(file);
      }
    } catch (_) {
      setError('Could not read file');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Step 2: cropper returns a cropped Blob → upload it to the backend
  const handleCropConfirm = async (blob) => {
    setCropSrc(null);
    setUploadingCover(true);
    setCoverProgress(0);
    try {
      const form = new FormData();
      form.append('file', blob, 'cover.jpg');
      const { data } = await axios.post(`${API_URL}/api/auras/cover-upload`, form, {
        withCredentials: true,
        onUploadProgress: (evt) => {
          if (evt.total) setCoverProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });
      setCoverImage(`${API_URL}${data.url}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Cover upload failed');
    } finally {
      setUploadingCover(false);
      setCoverProgress(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (privacyMode === 'locked' && !password) {
      setError('Password is required for locked auras');
      return;
    }

    if (multiDay && endDate && endDate < eventDate) {
      setError('End date must be on or after the start date');
      return;
    }

    setLoading(true);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/auras`,
        {
          title,
          description,
          event_date: eventDate,
          end_date: multiDay && endDate ? endDate : null,
          occasion: occasion || null,
          vibe,
          privacy_mode: privacyMode,
          password: privacyMode === 'locked' ? password : null,
          cover_image: coverImage,
          primary_color: '#1A1B2E',
          accent_color: '#D4AF37'
        },
        { withCredentials: true }
      );
      navigate(`/aura/${data.id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create aura');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-midnight">
      {cropSrc && (
        <CoverCropper
          imageSrc={cropSrc}
          onConfirm={handleCropConfirm}
          onCancel={() => setCropSrc(null)}
        />
      )}
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-aura-purple/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-aura-gold/10 rounded-full blur-3xl" />
      </div>

      <SiteHeader />
      <div className="border-b border-champagne/10 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-end">
          <Link
            to="/dashboard"
            className="flex items-center gap-2 text-text-muted hover:text-champagne transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </Link>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-3xl sm:text-4xl font-serif font-medium text-text-light mb-2">
            Create a New Aura
          </h1>
          <p className="text-text-muted mb-8">
            Set up your event in less than 60 seconds.
          </p>

          {error && (
            <div className="bg-error/10 border border-error/30 text-error px-4 py-3 rounded-lg mb-6" data-testid="create-aura-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-text-light mb-2">
                Event Name *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="aura-input"
                placeholder="The Grand Gala 2026"
                required
                data-testid="aura-title-input"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-text-light mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="aura-input resize-none"
                placeholder="Share your favorite moments from this event..."
                rows={3}
                data-testid="aura-description-input"
              />
            </div>

            {/* Event Date */}
            <div>
              <label className="block text-sm font-medium text-text-light mb-2">
                <Calendar className="w-4 h-4 inline mr-2" />
                Event Date *
              </label>
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                onClick={(e) => { try { e.currentTarget.showPicker(); } catch {} }}
                onFocus={(e) => { try { e.currentTarget.showPicker(); } catch {} }}
                min={new Date().toISOString().split('T')[0]}
                className="aura-input cursor-pointer [color-scheme:dark]"
                required
                data-testid="aura-date-input"
              />

              {/* Multi-day toggle — e.g. a vacation/trip spanning several days */}
              <label className="flex items-center gap-2 mt-3 text-sm text-text-muted cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={multiDay}
                  onChange={(e) => {
                    setMultiDay(e.target.checked);
                    if (!e.target.checked) setEndDate('');  // clear stale end date
                  }}
                  className="accent-champagne"
                  data-testid="aura-multiday-toggle"
                />
                This event spans multiple days (e.g. a vacation)
              </label>

              {multiDay && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-text-light mb-2">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    onClick={(e) => { try { e.currentTarget.showPicker(); } catch {} }}
                    onFocus={(e) => { try { e.currentTarget.showPicker(); } catch {} }}
                    min={eventDate || new Date().toISOString().split('T')[0]}
                    className="aura-input cursor-pointer [color-scheme:dark]"
                    required={multiDay}
                    data-testid="aura-end-date-input"
                  />
                </div>
              )}
            </div>

            {/* Occasion (optional) */}
            <div>
              <label className="block text-sm font-medium text-text-light mb-2">
                Occasion <span className="text-text-muted font-normal">(optional)</span>
              </label>
              <select
                value={occasion}
                onChange={(e) => setOccasion(e.target.value)}
                className="aura-input cursor-pointer [color-scheme:dark]"
                data-testid="aura-occasion-select"
              >
                {OCCASIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Vibe Selection */}
            <div>
              <label className="block text-sm font-medium text-text-light mb-3">
                Select the Vibe
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {VIBES.map((v) => (
                  <button
                    key={v.value}
                    type="button"
                    onClick={() => setVibe(v.value)}
                    className={`p-4 rounded-xl border transition-all text-center ${
                      vibe === v.value
                        ? 'border-champagne bg-champagne/10 text-champagne'
                        : 'border-champagne/20 hover:border-champagne/40 text-text-muted'
                    }`}
                    data-testid={`vibe-${v.value}`}
                  >
                    <v.Icon className="w-7 h-7 mx-auto mb-1" />
                    <span className="text-sm font-medium">{v.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Privacy Mode */}
            <div>
              <label className="block text-sm font-medium text-text-light mb-3">
                Privacy Mode
              </label>
              <div className="space-y-3">
                {PRIVACY_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setPrivacyMode(mode.value)}
                    className={`w-full p-4 rounded-xl border transition-all flex items-center gap-4 text-left ${
                      privacyMode === mode.value
                        ? 'border-champagne bg-champagne/10'
                        : 'border-champagne/20 hover:border-champagne/40'
                    }`}
                    data-testid={`privacy-${mode.value}`}
                  >
                    <div className={`${privacyMode === mode.value ? 'text-champagne' : 'text-text-muted'}`}>
                      {mode.icon}
                    </div>
                    <div>
                      <p className={`font-medium ${privacyMode === mode.value ? 'text-champagne' : 'text-text-light'}`}>
                        {mode.label}
                      </p>
                      <p className="text-sm text-text-muted">{mode.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Password (for locked mode) */}
            {privacyMode === 'locked' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <label className="block text-sm font-medium text-text-light mb-2">
                  <Lock className="w-4 h-4 inline mr-2" />
                  Portal Password *
                </label>
                <input
                  type="text"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="aura-input"
                  placeholder="Enter password for guests"
                  data-testid="aura-password-input"
                />
              </motion.div>
            )}

            {/* Cover Image — upload or URL */}
            <div>
              <label className="block text-sm font-medium text-text-light mb-2">
                <Image className="w-4 h-4 inline mr-2" />
                Cover Image (optional)
              </label>

              <div className="flex flex-col sm:flex-row gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingCover}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-champagne/30 text-champagne text-sm font-medium hover:bg-champagne/10 disabled:opacity-50"
                  data-testid="aura-cover-upload-btn"
                >
                  <Upload className="w-4 h-4" />
                  {uploadingCover ? 'Uploading…' : 'Upload Image'}
                </button>
                <span className="text-text-muted text-xs self-center">or paste a URL below</span>
              </div>
              {uploadingCover && (
                <div className="mb-2">
                  <div className="h-1.5 w-full bg-white/10 rounded overflow-hidden">
                    <div className="h-full bg-champagne transition-all" style={{ width: `${coverProgress}%` }} />
                  </div>
                  <div className="text-xs text-text-muted mt-1">{coverProgress}%</div>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverFile}
                className="hidden"
                data-testid="aura-cover-file-input"
              />

              <input
                type="text"
                value={coverImage}
                onChange={(e) => setCoverImage(e.target.value)}
                className="aura-input"
                placeholder="https://example.com/image.jpg or /uploads/cover_xxx.jpg"
                data-testid="aura-cover-input"
              />

              {coverImage && (
                <div className="mt-3 space-y-2">
                  {/* Honest preview at the actual 16:9 aspect we're saving */}
                  <div className="rounded-xl overflow-hidden border border-champagne/20 relative bg-midnight-light/40" style={{ aspectRatio: '16/9' }}>
                    <img
                      src={coverImage}
                      alt="Cover preview"
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                    <div className="absolute top-2 right-2 flex gap-2">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="px-3 py-1 rounded-full bg-midnight/80 hover:bg-champagne/80 hover:text-midnight text-text-light text-xs font-medium"
                        title="Replace cover" aria-label="Replace cover"
                      >
                        Replace
                      </button>
                      <button
                        type="button"
                        onClick={() => setCoverImage('')}
                        className="p-2.5 rounded-full bg-midnight/80 hover:bg-error/80 text-text-light min-w-[44px] min-h-[44px] flex items-center justify-center"
                        title="Remove cover" aria-label="Remove cover"
                        data-testid="aura-cover-clear"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-text-muted text-xs">This is the cropped 16:9 cover that will be saved with your aura.</p>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-champagne flex items-center justify-center gap-2 py-4"
              data-testid="create-aura-submit-btn"
            >
              {loading ? (
                <span className="animate-pulse">Creating your aura...</span>
              ) : (
                <>
                  Create Aura
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </motion.div>
      </div>
      <FooterExpanded />
    </div>
  );
}
